import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/80",
        warning:
          "border-transparent bg-amber-500 text-white shadow-sm hover:bg-amber-500/80",
        info: "border-transparent bg-blue-600 text-white shadow-sm hover:bg-blue-600/80",
        success:
          "border-transparent bg-green-600 text-white shadow-sm hover:bg-green-600/80",
        neutral:
          "border-transparent bg-muted text-muted-foreground dark:bg-foreground dark:text-background",
        inverted: "border-transparent bg-foreground text-background",
        "soft-success":
          "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/15 dark:border-emerald-400/30 dark:bg-emerald-400/10 dark:text-emerald-400 dark:hover:bg-emerald-400/15",
        "soft-warning":
          "border-amber-500/30 bg-amber-500/10 text-amber-700 hover:bg-amber-500/15 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-400 dark:hover:bg-amber-400/15",
        "soft-danger":
          "border-red-500/30 bg-red-500/10 text-red-700 hover:bg-red-500/15 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-400 dark:hover:bg-red-400/15",
        "soft-neutral":
          "border-slate-500/30 bg-slate-500/10 text-slate-700 hover:bg-slate-500/15 dark:border-slate-400/30 dark:bg-slate-400/10 dark:text-slate-300 dark:hover:bg-slate-400/15",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
